/**
 * @name HomePage
 * @description Dashboard principal melhorado para responsáveis - IS Cantina Client Web
 * @author Gabriel M. Guimarães | gabrielmg7
 */

import PageContainer from '@/components/layouts/PageContainer';
import { AccountBalance as AccountBalanceIcon, AccountBalanceWallet as AccountBalanceWalletIcon, AddCircle as AddCircleIcon, Home as HomeIcon, People as PeopleIcon, School as SchoolIcon, TrendingUp as TrendingUpIcon, Build as BuildIcon } from '@mui/icons-material';
import { Box, Card, CardContent, Chip, CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HomePageDependentesGastos, HomePageEvolutionChart, HomePageProdutosFavoritos } from '.';
import { HomePageExtratoMovimentacoesDialog } from './HomePageExtratoMovimentacoesDialog';
import { useHomePage } from './useHomePage';

interface IQuickAction {
     title: string;
     description: string;
     icon: React.ReactNode;
     path?: string;
     onClick?: () => void;
     color: string;
}

export const HomePage: React.FC = () => {
     const navigate = useNavigate();
     const [openExtratoMovimentacoes, setOpenExtratoMovimentacoes] = useState(false);
     const { gastosPorDependente, evolucaoGastos, produtosFavoritos, nomesDependentes, loading, metadados, carregarMetricasDashboard } = useHomePage();

     // 🚧 FLAG DE MANUTENÇÃO - Alterar para true para desativar recargas temporariamente
     const RECARGA_EM_MANUTENCAO = false;

     const quickActions: IQuickAction[] = [
          {
               title: 'Minha Carteira',
               description: RECARGA_EM_MANUTENCAO ? 'Visualizar saldo e histórico' : 'Visualizar saldo e recarregar via PIX',
               icon: <AccountBalanceIcon sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }} />,
               path: '/creditos',
               color: '#4caf50',
          },
          {
               title: 'Transferir Créditos',
               description: 'Enviar saldo para seus dependentes',
               icon: <TrendingUpIcon sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }} />,
               path: '/creditos/transferir',
               color: '#2196f3',
          },
          {
               title: 'Meus Dependentes',
               description: 'Gerenciar filhos e dependentes',
               icon: <PeopleIcon sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }} />,
               path: '/dependentes',
               color: '#ff9800',
          },
          {
               title: 'Extrato de Movimentações',
               description: 'Consultar créditos e débitos detalhados',
               icon: <AccountBalanceWalletIcon sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }} />,
               onClick: () => setOpenExtratoMovimentacoes(true),
               color: '#673ab7',
          },
          {
               title: RECARGA_EM_MANUTENCAO ? '🚧 Recarregar Saldo' : 'Recarregar Saldo',
               description: RECARGA_EM_MANUTENCAO ? 'Em manutenção temporariamente' : 'Adicionar créditos via PIX',
               icon: RECARGA_EM_MANUTENCAO ? <BuildIcon sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }} /> : <AddCircleIcon sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }} />,
               path: '/creditos',
               color: RECARGA_EM_MANUTENCAO ? '#ff9800' : '#f44336',
          },
     ];

     if (loading) {
          return (
               <PageContainer
                    title={metadados.title}
                    description={metadados.description}
                    icon={<HomeIcon />}
                    breadcrumbs={[{ label: 'Dashboard', path: '/' }]}
               >
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                         <CircularProgress size={60} />
                    </Box>
               </PageContainer>
          );
     }

     return (
          <PageContainer
               title={metadados.title}
               description={metadados.description}
               icon={<HomeIcon />}
               breadcrumbs={[{ label: 'Dashboard', path: '/' }]}
          >
               {/* Header de Boas-vindas */}
               <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                    <Card
                         sx={{
                              background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                              color: 'white',
                              mb: 3,
                         }}
                    >
                         <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                   <SchoolIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                                   <Typography
                                        variant='h5'
                                        fontWeight='bold'
                                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                                   >
                                        Olá, {metadados.userName}!
                                   </Typography>
                                   <Chip
                                        label={metadados.userTypeInfo.label}
                                        color={metadados.userTypeInfo.color}
                                        size='small'
                                   />
                              </Box>

                              {/* Nomes dos dependentes */}
                              {nomesDependentes.length > 0 && (
                                   <Box sx={{ mb: 2 }}>
                                        <Typography
                                             variant='body2'
                                             sx={{
                                                  opacity: 0.8,
                                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                  fontWeight: 500,
                                             }}
                                        >
                                             Dependentes: {nomesDependentes.join(', ')}
                                        </Typography>
                                   </Box>
                              )}

                              <Typography
                                   variant='body1'
                                   sx={{
                                        opacity: 0.9,
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                   }}
                              >
                                   Gerencie os créditos dos seus dependentes, acompanhe as compras na cantina escolar e tenha controle total dos gastos em tempo real.
                              </Typography>
                         </CardContent>
                    </Card>
               </Box>

               {/* Acessos Rápidos - Movido para antes das métricas */}
               <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                    <Typography
                         variant='h6'
                         gutterBottom
                         fontWeight='bold'
                    >
                         Acessos Rápidos
                    </Typography>
                    <Grid
                         container
                         spacing={{ xs: 1.5, sm: 2 }}
                    >
                         {quickActions.map((action, index) => {
                              const isRecarregaEmManutencao = RECARGA_EM_MANUTENCAO && action.title.includes('Recarregar');
                              const cardContent = (
                                   <Card
                                        sx={{
                                             height: '100%',
                                             cursor: 'pointer',
                                             transition: 'all 0.3s ease',
                                             opacity: isRecarregaEmManutencao ? 0.85 : 1,
                                             '&:hover': {
                                                  transform: 'translateY(-4px)',
                                                  boxShadow: 4,
                                             },
                                        }}
                                   >
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                                             <Box
                                                  sx={{
                                                       color: action.color,
                                                       mb: { xs: 0.5, sm: 1 },
                                                       display: 'flex',
                                                       justifyContent: 'center',
                                                  }}
                                             >
                                                  {action.icon}
                                             </Box>
                                             <Typography
                                                  variant='subtitle2'
                                                  fontWeight='bold'
                                                  gutterBottom
                                                  sx={{
                                                       fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                       lineHeight: 1.2,
                                                       mb: { xs: 0.5, sm: 1 },
                                                  }}
                                             >
                                                  {action.title}
                                             </Typography>
                                             <Typography
                                                  variant='caption'
                                                  color='text.secondary'
                                                  sx={{
                                                       fontSize: { xs: '0.625rem', sm: '0.75rem' },
                                                       display: { xs: 'none', sm: 'block' },
                                                  }}
                                             >
                                                  {action.description}
                                             </Typography>
                                        </CardContent>
                                   </Card>
                              );

                              return (
                                   <Grid
                                        item
                                        xs={6}
                                        sm={4}
                                        md={3}
                                        key={index}
                                   >
                                        {isRecarregaEmManutencao ? (
                                             <Tooltip
                                                  title='A funcionalidade de recarga está temporariamente em manutenção. Você pode visualizar seu saldo na página de créditos.'
                                                  arrow
                                                  placement='top'
                                             >
                                                  {cardContent}
                                             </Tooltip>
                                        ) : (
                                             <Box
                                                  onClick={() => {
                                                       if (action.onClick) {
                                                            action.onClick();
                                                            return;
                                                       }

                                                       if (action.path) {
                                                            navigate(action.path);
                                                       }
                                                  }}
                                             >
                                                  {cardContent}
                                             </Box>
                                        )}
                                   </Grid>
                              );
                         })}
                    </Grid>
               </Box>

               {/* Gráficos e Dados */}
               <Grid
                    container
                    spacing={{ xs: 2, sm: 3 }}
                    sx={{ mt: { xs: 1, sm: 2 } }}
               >
                    {/* Evolução de Gastos */}
                    <Grid
                         item
                         xs={12}
                         md={8}
                    >
                         <HomePageEvolutionChart
                              data={evolucaoGastos}
                              title='Evolução de Gastos (Últimos 6 Meses)'
                              onRefresh={carregarMetricasDashboard}
                         />
                    </Grid>

                    {/* Gastos por Dependente */}
                    <Grid
                         item
                         xs={12}
                         md={4}
                    >
                         <HomePageDependentesGastos
                              data={gastosPorDependente}
                              title='Gastos por Dependente'
                         />
                    </Grid>

                    {/* Produtos Mais Consumidos */}
                    <Grid
                         item
                         xs={12}
                    >
                         <HomePageProdutosFavoritos
                              data={produtosFavoritos}
                              title='Produtos Mais Consumidos'
                         />
                    </Grid>
               </Grid>

               <HomePageExtratoMovimentacoesDialog
                    open={openExtratoMovimentacoes}
                    onClose={() => setOpenExtratoMovimentacoes(false)}
               />
          </PageContainer>
     );
};
